import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarCheck2, ChartLine } from "lucide-react";
import { useLeaveRequests } from "../hooks";
import { LeaveRequestCard } from "@/features/shifts/components/LeaveRequestCard";
import type { LeaveRequestDisplayItem } from "@/features/shifts/types";
import { leaveRequestToDisplayItem } from "@/features/shifts/utils/leaveRequestAdapter";
import { LeaveRequestModal } from "../components/LeaveRequestModal";
import { LeaveRequestDetailsModal } from "@/features/shifts/components/LeaveRequestDetailsModal";
import { config } from "@/lib/config";

type LeaveFilter = "all" | "upcoming" | "past";

function isPast(endDate: string): boolean {
  const end = new Date(`${endDate}T23:59:59.999Z`).getTime();
  return Number.isFinite(end) && end < Date.now();
}

function isUpcoming(endDate: string): boolean {
  return !isPast(endDate);
}

export function LeaveHistoryPage() {
  const { data, isLoading, error } = useLeaveRequests();
  const [filter, setFilter] = useState<LeaveFilter>("all");
  const [editingLeaveRequest, setEditingLeaveRequest] =
    useState<LeaveRequestDisplayItem | null>(null);
  const [detailsLeaveRequest, setDetailsLeaveRequest] =
    useState<LeaveRequestDisplayItem | null>(null);

  const requests = data?.requests ?? [];
  const displayItems = useMemo(
    () => requests.map((request) => leaveRequestToDisplayItem(request)),
    [requests]
  );

  const filteredItems = useMemo(() => {
    switch (filter) {
      case "upcoming":
        return displayItems.filter((item) => isUpcoming(item.end_date));
      case "past":
        return displayItems.filter((item) => isPast(item.end_date));
      case "all":
      default:
        return displayItems;
    }
  }, [displayItems, filter]);

  const handleOpenPendingLeaveApproval = (request: LeaveRequestDisplayItem) => {
    if (request.status !== "pending") return;
    const baseUrl = config.app.leaveApprovalsUrl;
    if (!baseUrl) return;
    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}leaveRequestId=${request.leave_request_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col h-full min-h-0 w-full max-w-full min-w-0 overflow-x-hidden bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        <section className="px-4 pt-4 pb-2">
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 m-0">
            Leave History
          </h1>
          <p className="text-slate-500 mt-1">View all your past and upcoming leave</p>
        </section>

        <section className="px-4 py-3 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-emerald-500 text-white p-4">
            <div className="inline-flex rounded-lg p-2 bg-white/20 mb-3">
              <CalendarCheck2 className="h-4 w-4" />
            </div>
            <p className="text-sm text-white/80 m-0">Available</p>
            <p className="text-5xl font-semibold mt-2 mb-0">
              {data?.days_remaining ?? 0}
            </p>
            <p className="text-sm text-white/90 mt-1 mb-0">days remaining</p>
          </div>
          <div className="rounded-2xl bg-blue-600 text-white p-4">
            <div className="inline-flex rounded-lg p-2 bg-white/20 mb-3">
              <ChartLine className="h-4 w-4" />
            </div>
            <p className="text-sm text-white/80 m-0">Last 18 months</p>
            <p className="text-5xl font-semibold mt-2 mb-0">
              {data?.days_taken_last_18_months ?? 0}
            </p>
            <p className="text-sm text-white/90 mt-1 mb-0">days taken</p>
          </div>
        </section>

        <section className="px-4 pt-1 pb-3 flex gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            className="flex-1 rounded-xl"
            onClick={() => setFilter("all")}
            data-testid="leave-history-filter-all"
          >
            All
          </Button>
          <Button
            variant={filter === "upcoming" ? "default" : "outline"}
            className="flex-1 rounded-xl"
            onClick={() => setFilter("upcoming")}
            data-testid="leave-history-filter-upcoming"
          >
            Upcoming
          </Button>
          <Button
            variant={filter === "past" ? "default" : "outline"}
            className="flex-1 rounded-xl"
            onClick={() => setFilter("past")}
            data-testid="leave-history-filter-past"
          >
            Past
          </Button>
        </section>

        <section className="px-4 pb-6 space-y-3">
          {isLoading ? (
            <div className="rounded-xl bg-white border border-slate-200 p-4 text-sm text-slate-500">
              Loading leave requests...
            </div>
          ) : null}
          {error ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              Failed to load leave requests.
            </div>
          ) : null}
          {!isLoading && !error && filteredItems.length === 0 ? (
            <div className="rounded-xl bg-white border border-slate-200 p-4 text-sm text-slate-500">
              No leave requests found for this filter.
            </div>
          ) : null}

          {filteredItems.map((item) => (
            <LeaveRequestCard
              key={item.leave_request_id}
              request={item}
              onEdit={(request) => setEditingLeaveRequest(request)}
              onOpenPendingApproval={handleOpenPendingLeaveApproval}
              onViewDetails={(request) => setDetailsLeaveRequest(request)}
            />
          ))}
        </section>
      </div>

      <LeaveRequestModal
        open={editingLeaveRequest != null}
        onOpenChange={(open) => {
          if (!open) setEditingLeaveRequest(null);
        }}
        editingRequest={editingLeaveRequest}
      />
      <LeaveRequestDetailsModal
        open={detailsLeaveRequest != null}
        onOpenChange={(open) => {
          if (!open) setDetailsLeaveRequest(null);
        }}
        request={detailsLeaveRequest}
      />
    </div>
  );
}
